import { useState, type SyntheticEvent } from 'react';
import { fetchVerify, toolErrorText, type VerifyPayload } from '../api/tools';
import { ADDRESS_RE, MAINNET, NVDA } from '../lib/chain';
import { AddrLink } from './AddrLink';

type State =
  | { s: 'idle' }
  | { s: 'invalid' }
  | { s: 'loading'; addr: string }
  | { s: 'done'; v: VerifyPayload }
  | { s: 'failed'; addr: string; text: string };

const CHECK_LABELS: [keyof VerifyPayload['checks'], string][] = [
  ['codehash', 'proxy code is the issuer BeaconProxy'],
  ['beacon', "beacon slot points at the issuer's beacon"],
  ['create2', "address equals the factory's CREATE2 result for the reported uid"],
];

function Answer({ st }: { st: State }) {
  if (st.s === 'invalid') return <p>Enter a 20 byte hex address: 0x followed by 40 hex characters.</p>;
  if (st.s === 'loading') return <p>Asking the verifier about {st.addr}…</p>;
  if (st.s === 'failed') return <><h4>Could not check</h4><p>{st.text}</p><p className="muted">No verdict: an API or RPC failure is never reported as yes or no.</p></>;
  if (st.s !== 'done') return null;
  const v = st.v;
  return (
    <>
      <h4>{v.official ? 'Official: yes' : 'Official: no'}</h4>
      <dl>
        <dt>Address</dt><dd>{v.address}</dd>
        <dt>Reason</dt><dd>{v.reason || 'none given'}</dd>
        {CHECK_LABELS.map(([k, label]) => (
          <div key={k} style={{ display: 'contents' }}><dt>Check: {k === 'create2' ? 'CREATE2' : k}</dt><dd className={v.checks[k] ? 'hot' : ''}>{v.checks[k] === null ? 'informational' : v.checks[k] ? 'pass' : 'fail'}<span className="muted">, {label}</span></dd></div>
        ))}
        <dt>Verifier</dt><dd>{v.verifier ? <AddrLink addr={v.verifier} /> : 'n/a'}</dd>
        <dt>Block</dt><dd>{String(v.blockNumber) || 'n/a'} (chain {v.chainId || MAINNET})</dd>
      </dl>
      <p>Measured: the answer of the StockVerifier contract on Robinhood Chain mainnet at that block, read by the worker API. A yes proves factory provenance, not backing, redemption or safety.</p>
    </>
  );
}

/** "Verify a token": GET /api/data/verify/:address?chainId=4663 and the verifier's answer. */
export function VerifyToken() {
  const [addr, setAddr] = useState('');
  const [st, setSt] = useState<State>({ s: 'idle' });

  const run = (a: string) => {
    if (!ADDRESS_RE.test(a)) { setSt({ s: 'invalid' }); return; }
    setSt({ s: 'loading', addr: a });
    fetchVerify(a, MAINNET)
      .then((v) => { setSt({ s: 'done', v }); })
      .catch((e: unknown) => { setSt({ s: 'failed', addr: a, text: toolErrorText(e) }); });
  };
  const onSubmit = (ev: SyntheticEvent<HTMLFormElement>) => { ev.preventDefault(); run(addr.trim()); };
  const cls = st.s === 'done' ? (st.v.official ? 'result ok' : 'result no') : st.s === 'failed' || st.s === 'invalid' ? 'result unknown' : 'result';

  return (
    <section className="section" id="verify">
      <h2>Verify a token</h2>
      <p className="table-intro">Paste a token address on Robinhood Chain mainnet (4663). The worker API asks the deployed StockVerifier and returns its answer with the three checks behind it: the proxy's code, the issuer's beacon, and the CREATE2 address the issuer's factory would produce. The issuer's factory has its own lookup; this answer comes from a recomputation that does not depend on it.</p>
      <form className="checker" id="verify-form" onSubmit={onSubmit}>
        <label htmlFor="verify-addr" className="sr-only">Token address</label>
        <input id="verify-addr" type="text" autoComplete="off" spellCheck="false" placeholder="0x…" value={addr} onChange={(e) => { setAddr(e.target.value); }} />
        <button type="submit" className="button" disabled={st.s === 'loading'}>Verify</button>
      </form>
      <p className="examples">Try <button type="button" className="linkish" onClick={() => { setAddr(NVDA); run(NVDA); }}>the official NVDA token</button>.</p>
      <div className={cls} id="verify-result" aria-live="polite" hidden={st.s === 'idle'}><Answer st={st} /></div>
    </section>
  );
}
